import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useDatabase } from '../context/DatabaseContext';

const { FiCircle, FiArrowRight, FiFilter, FiInfo, FiTool } = FiIcons;

const Bearings = () => {
  const { products } = useDatabase();
  const [selectedType, setSelectedType] = useState('all');

  // Filter nur Wälzlager (EC0000xx)
  const bearingProducts = products.filter(p => p.etim_class_code.startsWith('EC0000'));

  const bearingTypes = {
    'all': 'Alle Wälzlager',
    'EC000030': 'Rillenkugellager',
    'EC000031': 'Schrägkugellager',
    'EC000032': 'Nadellager',
    'EC000033': 'Axiallager',
    'EC000034': 'Gehäuselager',
    'EC000035': 'Kegelrollenlager'
  };

  const filteredProducts = selectedType === 'all' 
    ? bearingProducts 
    : bearingProducts.filter(p => p.etim_class_code === selectedType);

  const getBearingTypeDescription = (type) => {
    const descriptions = {
      'EC000030': 'Standardlager für radiale Belastungen. Punktberührung zwischen Wälzkörpern und Laufbahnen ermöglicht hohe Drehzahlen bei moderaten Lasten.',
      'EC000031': 'Für kombinierte radial-axiale Belastungen. Druckwinkel zwischen 15° und 40° je nach Anwendung.',
      'EC000032': 'Kompakte Bauform für hohe Radiallasten bei minimalem Bauraum. Linienberührung für hohe Tragfähigkeit.',
      'EC000033': 'Speziell für axiale Belastungen (Schubkräfte). Nicht für reine Radialkräfte geeignet.',
      'EC000034': 'Komplette Lagereinheiten mit Gehäuse. Selbstausrichtend und einfach zu montieren.',
      'EC000035': 'Kegelförmige Wälzkörper für hohe kombinierte Belastungen und Steifigkeit.'
    };
    return descriptions[type] || 'Hochwertige Wälzlager für industrielle Anwendungen.';
  };

  const getTechnicalHighlights = (type) => {
    const highlights = {
      'EC000030': ['Hohe Drehzahlen', 'Geringe Reibung', 'Vielseitig einsetzbar'],
      'EC000031': ['Axial-radiale Belastung', 'Präzisionslagerung', 'Steife Lagerung'],
      'EC000032': ['Minimaler Bauraum', 'Hohe Tragzahl', 'Gehärtete Welle erforderlich'],
      'EC000033': ['Reine Axialkräfte', 'Hohe Schubbelastung', 'Kompakte Bauform'],
      'EC000034': ['Plug & Play', 'Selbstausrichtend', 'Robuste Konstruktion'],
      'EC000035': ['Höchste Steifigkeit', 'Einstellbare Vorspannung', 'Schwere Lasten']
    };
    return highlights[type] || ['Zuverlässig', 'Langlebig', 'Präzise'];
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Wälzlagertechnik</h1>
            <p className="text-gray-600 mt-2">
              Präzisions-Wälzlager für industrielle Anwendungen nach ISO-Standards
            </p>
          </div>
          <div className="flex items-center space-x-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg border border-red-100">
            <SafeIcon icon={FiCircle} className="w-5 h-5" />
            <span className="font-medium">{bearingProducts.length} Lager</span>
          </div>
        </div>

        {/* Bearing Type Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <SafeIcon icon={FiFilter} className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Lagertypen</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {Object.entries(bearingTypes).map(([code, name]) => (
              <button
                key={code}
                onClick={() => setSelectedType(code)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedType === code
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        {/* Technical Information for Selected Type */}
        {selectedType !== 'all' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiCircle} className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {bearingTypes[selectedType]}
                </h3>
                <p className="text-gray-600 mb-4">
                  {getBearingTypeDescription(selectedType)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {getTechnicalHighlights(selectedType).map((highlight, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                    >
                      {highlight}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiCircle} className="w-6 h-6 text-gray-400" />
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  product.availability === 'In Stock' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {product.availability === 'In Stock' ? 'Verfügbar' : 'Nicht verfügbar'}
                </span>
              </div>

              <h3 className="font-semibold text-gray-900 mb-1">
                {product.manufacturer_sku}
              </h3>
              <p className="text-sm text-gray-600 mb-3">{product.manufacturer}</p>

              <div className="space-y-2 mb-4">
                {product.specifications.inner_diameter_mm && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Innen-Ø:</span>
                    <span className="font-medium">{product.specifications.inner_diameter_mm} mm</span>
                  </div>
                )}
                {product.specifications.outer_diameter_mm && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Außen-Ø:</span>
                    <span className="font-medium">{product.specifications.outer_diameter_mm} mm</span>
                  </div>
                )}
                {product.specifications.width_mm && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Breite:</span>
                    <span className="font-medium">{product.specifications.width_mm} mm</span>
                  </div>
                )}
                {product.specifications.dynamic_load_rating_n && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">C dyn:</span>
                    <span className="font-medium">{(product.specifications.dynamic_load_rating_n / 1000).toFixed(1)} kN</span>
                  </div>
                )}
              </div>

              <Link
                to={`/product/${product.id}`}
                className="flex items-center justify-between w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                <span>Details anzeigen</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Technical Standards */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Technische Standards</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Lebensdauerberechnung</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• ISO 281 - Nominelle Lebensdauer L10</li>
                <li>• Erweiterte Lebensdauer L10m</li>
                <li>• Schmierungsbeiwert aISO</li>
                <li>• Materialbeiwert a23</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Toleranzen & Passungen</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• ISO 492 - Laufgenauigkeit</li>
                <li>• Toleranzklassen P0, P6, P5, P4</li>
                <li>• Wellenpassungen h6, js6, k6, m6, n6</li>
                <li>• Gehäusepassungen H7, J7, K7, M7, N7</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Schmierung & Wartung</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Fettschmierung für Standardanwendungen</li>
                <li>• Ölschmierung bei hohen Drehzahlen</li>
                <li>• Nachschmierungsintervalle</li>
                <li>• Überwachung von Temperatur & Schwingung</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Bearings;